/**
 * Role configuration — single source of truth for role-aware UI/agent behavior.
 *
 * Each role drives:
 *   - Top-nav role switcher label/icon
 *   - Welcome copy in the empty chat state
 *   - Quick-action cards
 *   - Accent color (CSS custom property)
 *   - Agent context payload (sent with every chat.send so the agent adapts)
 */

import type { UserRole } from '../types/gateway';

export interface RoleQuickAction {
  /** Action title shown on the card */
  title: string;
  /** Short explanation under the title */
  description: string;
  /** Emoji / unicode glyph icon */
  icon: string;
  /** Prompt text injected into the input when card is clicked */
  prompt: string;
}

export interface RoleConfig {
  key: UserRole;
  /** Short label e.g. "Field Contractor" */
  label: string;
  /** Glyph displayed in the role switcher */
  icon: string;
  /** Accent CSS variable name (without `--` prefix) used to theme the UI */
  accent: 'amber' | 'purple' | 'green' | 'blue';
  /** One-line description shown under the label in the dropdown */
  description: string;
  /** "Goal" line sent to the agent on every message */
  goal: string;
  /** Welcome line shown in the empty chat state */
  welcomeHeadline: string;
  /** Sub-line describing how Foreman helps in this role */
  welcomeSubline: string;
  /** Behavior contract sent to the agent so it adapts tone/output */
  expectedBehavior: string;
  /** Cards rendered when chat is empty */
  quickActions: RoleQuickAction[];
}

const FIELD: RoleConfig = {
  key: 'field',
  label: 'Field Contractor',
  icon: '\u{1F6E0}\u{FE0F}', // hammer-and-wrench
  accent: 'amber',
  description: 'Capture job details and send to office',
  goal: 'Capture job details and send to office',
  welcomeHeadline: "Hey {name}, I\u2019m Foreman.",
  welcomeSubline: "I\u2019ll help you capture job details and send them to the office.",
  expectedBehavior:
    'Ask simple follow-up questions, help verify measurements, remind the user about missing photos/details, and confirm when the office has everything needed.',
  quickActions: [
    {
      title: 'Send Measurements',
      description: 'Capture and send measurements',
      icon: '\u{1F4CF}',
      prompt: 'I want to send measurements for a job. ',
    },
    {
      title: 'Upload Job Photos',
      description: 'Add photos from the job site',
      icon: '\u{1F4F8}',
      prompt: 'I want to upload photos from the job site. ',
    },
    {
      title: 'Submit Job Details',
      description: 'Send job scope and notes to office',
      icon: '\u{1F4CB}',
      prompt: 'I have job details and notes to send to the office. ',
    },
    {
      title: 'Check My Submissions',
      description: 'View status of submitted jobs',
      icon: '\u{1F4E6}',
      prompt: 'Show the status of jobs I\u2019ve submitted.',
    },
  ],
};

const DESIGNER: RoleConfig = {
  key: 'designer',
  label: 'Kitchen Designer',
  icon: '\u{1F3A8}', // artist palette
  accent: 'purple',
  description: 'Design kitchens and build scopes',
  goal: 'Design kitchens and build scopes',
  welcomeHeadline: "Hey {name}, I\u2019m Foreman.",
  welcomeSubline: "I\u2019ll help turn ideas into scopes, drawings, and material plans.",
  expectedBehavior:
    'Help organize design requirements, identify missing information, suggest material options, flag scope gaps, and keep the design aligned with pricing needs.',
  quickActions: [
    {
      title: 'Create Kitchen Plan',
      description: 'Start a new design plan',
      icon: '\u{1F9F1}',
      prompt: 'Start a new kitchen design plan. ',
    },
    {
      title: 'Build Scope',
      description: 'Turn notes into a clean scope',
      icon: '\u{1F4C4}',
      prompt: 'Turn these notes into a clean scope of work: ',
    },
    {
      title: 'Suggest Materials',
      description: 'Recommend cabinets, counters, and fixtures',
      icon: '\u{1F9CA}',
      prompt: 'Suggest cabinets, counters, and fixtures for this kitchen: ',
    },
    {
      title: 'Review Drawings',
      description: 'Check plans for missing details',
      icon: '\u{1F5BC}\u{FE0F}',
      prompt: 'Review these drawings and flag anything missing: ',
    },
  ],
};

const ACCOUNTING: RoleConfig = {
  key: 'accounting',
  label: 'Accounting / Office',
  icon: '\u{1F4B0}', // money bag
  accent: 'green',
  description: 'Price jobs and manage bids',
  goal: 'Price jobs and manage bids',
  welcomeHeadline: "Hey {name}, I\u2019m Foreman.",
  welcomeSubline: "I\u2019ll help price jobs, manage bids, and track profitability.",
  expectedBehavior:
    'Pull together submitted details, calculate pricing, surface risks or missing data, show margin impact, and help create a polished final bid.',
  quickActions: [
    {
      title: 'Price This Job',
      description: 'Generate a detailed job estimate',
      icon: '\u{1F4B0}',
      prompt: 'Price this job for me: ',
    },
    {
      title: 'View Margins',
      description: 'Review labor, materials, and profit',
      icon: '\u{1F4CA}',
      prompt: 'Show labor, materials, and profit margins for ',
    },
    {
      title: 'Compare Options',
      description: 'Compare pricing alternatives',
      icon: '\u{2696}\u{FE0F}',
      prompt: 'Compare pricing alternatives for ',
    },
    {
      title: 'Finalize Bid',
      description: 'Prepare a clean customer-ready bid',
      icon: '\u{1F9FE}',
      prompt: 'Finalize a customer-ready bid for ',
    },
  ],
};

/** Admin keeps its own minimal config so legacy users with admin uiRole don't break. */
const ADMIN: RoleConfig = {
  key: 'admin',
  label: 'Admin',
  icon: '\u{1F511}',
  accent: 'blue',
  description: 'Manage training data and system config',
  goal: 'Manage training data and system config',
  welcomeHeadline: "Hey {name}, I\u2019m Foreman.",
  welcomeSubline: "Pick a role above to get role-tailored quick actions.",
  expectedBehavior:
    'Treat the user as an admin with access to training data and system management.',
  quickActions: [],
};

export const ROLE_CONFIG: Record<UserRole, RoleConfig> = {
  field: FIELD,
  designer: DESIGNER,
  accounting: ACCOUNTING,
  admin: ADMIN,
};

/** Roles that appear in the user-facing dropdown. Admin is handled elsewhere. */
export const SWITCHABLE_ROLES: RoleConfig[] = [FIELD, DESIGNER, ACCOUNTING];

export function getRoleConfig(role: UserRole): RoleConfig {
  return ROLE_CONFIG[role] || FIELD;
}

/** Build the agent context payload sent on every chat.send. */
export function buildAgentRoleContext(role: UserRole) {
  const cfg = getRoleConfig(role);
  return {
    active_role: cfg.key,
    role_label: cfg.label,
    role_goal: cfg.goal,
    expected_agent_behavior: cfg.expectedBehavior,
  };
}
